import type { FieldUnit, FieldUnitStats, TrailPoint, AuthHeadersFn, UnitStatus } from './unitsTypes';
import { API_URL } from '../../config';

async function fetchJson<T>(url: string, headers: HeadersInit): Promise<T> {
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

async function mutate<T>(url: string, method: string, headers: HeadersInit, body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

export interface UnitCreate {
  name: string;
  unit_type: FieldUnit['unit_type'];
  callsign?: string;
  status?: UnitStatus;
  latitude?: number | null;
  longitude?: number | null;
  home_latitude?: number | null;
  home_longitude?: number | null;
  description?: string;
  color?: string;
  icon?: string;
  metadata?: Record<string, unknown>;
}

export async function fetchUnits(
  h: AuthHeadersFn,
  params?: { unit_type?: string; status?: string; scope?: string },
): Promise<FieldUnit[]> {
  const sp = new URLSearchParams();
  if (params?.unit_type) sp.set('unit_type', params.unit_type);
  if (params?.status) sp.set('status', params.status);
  sp.set('scope', params?.scope || 'group');
  return fetchJson(`${API_URL}/api/field-units?${sp.toString()}`, h());
}

export async function fetchUnitById(h: AuthHeadersFn, id: number): Promise<FieldUnit> {
  return fetchJson(`${API_URL}/api/field-units/${id}`, h());
}

export async function createUnit(h: AuthHeadersFn, unit: UnitCreate, scope = 'group'): Promise<FieldUnit> {
  return mutate(`${API_URL}/api/field-units?scope=${scope}`, 'POST', h(true), unit);
}

export async function updateUnit(h: AuthHeadersFn, id: number, patch: Partial<UnitCreate>): Promise<FieldUnit> {
  return mutate(`${API_URL}/api/field-units/${id}`, 'PUT', h(true), patch);
}

export async function changeUnitStatus(
  h: AuthHeadersFn,
  id: number,
  status: UnitStatus,
  reason?: string,
): Promise<FieldUnit> {
  return mutate(`${API_URL}/api/field-units/${id}/status`, 'PUT', h(true), { status, reason });
}

export async function deleteUnit(h: AuthHeadersFn, id: number): Promise<{ deleted: boolean }> {
  return mutate(`${API_URL}/api/field-units/${id}`, 'DELETE', h());
}

export async function fetchUnitTrail(
  h: AuthHeadersFn,
  id: number,
  params?: { since?: string; limit?: number },
): Promise<{ unit_id: number; points: TrailPoint[] }> {
  const sp = new URLSearchParams();
  if (params?.since) sp.set('since', params.since);
  if (params?.limit) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return fetchJson(`${API_URL}/api/field-units/${id}/trail${qs ? `?${qs}` : ''}`, h());
}

export async function fetchUnitStats(h: AuthHeadersFn, scope = 'group'): Promise<FieldUnitStats> {
  return fetchJson(`${API_URL}/api/field-units/stats/summary?scope=${scope}`, h());
}
